// GLobal
import type { SearchResultsInitialState, SearchResultsStoreState } from '@sitecore-search/react';
import { WidgetDataType, widget } from '@sitecore-search/react';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

// Lib
import { BoKNavigation } from 'lib/templates/Feature.BOKF.model';
import { useSanitizedSearchResults } from 'lib/utils/token-replacer';
import { ArticleModel } from 'types/Search/Search.Models';

// Local
import SVG from 'helpers/SVG/SVG';
import { Spinner } from 'components/search';

export type SearchProps = BoKNavigation.SiteSearch;

type ArticleSearchResultsProps = {
  defaultPage?: SearchResultsStoreState['page'];
  defaultItemsPerPage?: SearchResultsStoreState['itemsPerPage'];
  defaultKeyphrase?: SearchResultsStoreState['keyphrase'];
  searchBarPlaceholderText?: string;
  searchFormTarget?: string;
  searchPageUrl?: string;
};

type InitialState = SearchResultsInitialState<'itemsPerPage' | 'keyphrase' | 'page'>;

const discoverSource = process.env.SEARCH_DISCOVER_URL;
const enhancedSearchSource = process.env.ENHANCED_SEARCH_SOURCE_ID?.split('|') || [];

export const SearchComponent = ({
  defaultPage = 1,
  defaultKeyphrase = '',
  defaultItemsPerPage = 10,
  searchBarPlaceholderText,
  searchFormTarget,
  searchPageUrl,
}: ArticleSearchResultsProps) => {
  const {
    widgetRef,
    state: { page },
    queryResult: { isLoading },
  } = useSanitizedSearchResults<ArticleModel, InitialState>({
    query: (query) => {
      query.getRequest().setSearchFacetAll(false).setSources(enhancedSearchSource);
    },
    state: {
      page: defaultPage,
      itemsPerPage: defaultItemsPerPage,
      keyphrase: defaultKeyphrase,
    },
  });

  const router = useRouter();

  let searchURL: URL | null = null;
  try {
    searchURL = new URL(searchFormTarget || '');
  } catch (error) {
    console.error(error);
  }

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const refSearchInput = useRef<HTMLInputElement>(null);

  const fetchSuggestions = async (query: string): Promise<string[]> => {
    const response = await fetch(discoverSource || '', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add Authorization header if required by your environment
        Authorization: `Bearer ${process.env.SITECORE_SEARCH_API_KEY}`,
      },
      body: JSON.stringify({
        context: {
          locale: {
            country: 'us',
            language: 'en',
          },
        },
        widget: {
          items: [
            {
              rfk_id: 'rfkid_7',
              entity: 'content',
              sources: enhancedSearchSource, // Use your actual source ID(s)
              search: {
                suggestion: [
                  {
                    max: 4,
                    name: 'esearch_autofill',
                    keyphrase_fallback: true,
                  },
                ],
                query: {
                  keyphrase: query,
                },
              },
            },
          ],
        },
      }),
    });

    const data = await response.json();
    // Extract suggestions from the response
    type Suggestion = { text: string };
    const suggestions =
      data.widgets?.[0]?.suggestion?.esearch_autofill?.map((s: Suggestion) => s.text) || [];
    return suggestions;
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setActiveIndex(-1);

    if (value.length > 2) {
      // Fetch suggestions only if input length > 2
      const fetchedSuggestions = await fetchSuggestions(value);
      setSuggestions(fetchedSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) return;

    // Handle arrow keys
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) {
        handleSuggestionClick(suggestions[activeIndex]);
      } else {
        handleSearchButtonClick(e);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setActiveIndex(-1);
    }
  };

  const renderSuggestions = () => {
    if (suggestions.length === 0) return null;

    return (
      <ul
        className="suggestions-list absolute top-0 w-full bg-light-background-neutral-variant z-[5] pt-[58px] pb-4 rounded-3xl"
        style={{
          boxShadow: '0 2px 10px 0px rgba(0,0,0,0.1)',
        }}
      >
        {suggestions.map((suggestion, index) => {
          // Bold the matching part of the suggestion
          const highlightedSuggestion = highlightMatch(suggestion, searchInputValue);
          const isActive = index === activeIndex;

          return (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              onKeyDown={(e) => {
                // Handle Enter key press
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault(); // Prevent default behavior
                  handleSuggestionClick(suggestion);
                }
              }}
              className={`suggestion-item text-p3 py-3 pl-5 outline-0 hover:bg-light-background-neutral-high hover:cursor-pointer focus-visible:bg-light-background-neutral-high ${
                isActive ? 'bg-light-background-neutral-high' : ''
              }`}
              tabIndex={0}
            >
              <span dangerouslySetInnerHTML={{ __html: highlightedSuggestion }} />
            </li>
          );
        })}
      </ul>
    );
  };

  // Function to highlight matching text with bold
  const highlightMatch = (text: string, query: string): string => {
    if (!query || query.length < 2) return text;

    // Escape special regex characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create a regex to find the query in the text (case insensitive)
    const regex = new RegExp(`(${escapedQuery})`, 'gi');

    // Replace matches with a bold version
    return text.replace(regex, '<strong>$1</strong>');
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchInputValue(suggestion);
    setSuggestions([]);
    handleSearchButtonClick({ preventDefault: () => {} }, suggestion);
  };

  const handleSearchButtonClick = (e: { preventDefault: () => void }, overrideValue?: string) => {
    e.preventDefault();
    const valueToSearch = overrideValue ?? searchInputValue;
    router.push(`${searchPageUrl}?q=${encodeURIComponent(valueToSearch)}&page=1`);
  };

  const handleClearSearchInput = () => {
    refSearchInput.current?.focus();
    setSearchInputValue('');
    setSuggestions([]);
    setActiveIndex(-1);
  };

  useEffect(() => {
    refSearchInput.current?.focus({ preventScroll: true });
  }, [page]);

  if (!searchURL) return <></>;

  if (isLoading) {
    return (
      <div className="fixed top-0 left-0 bottom-0 right-0 flex justify-center items-center h-full w-full bg-light-background-neutral-variant bg-opacity-90">
        <Spinner loading />
      </div>
    );
  }

  return (
    <section ref={widgetRef} className="container bokf-enhanced-search-landing">
      <div className="relative block w-full max-w-[582px] mx-auto">
        <input
          ref={refSearchInput}
          onKeyDown={handleKeyDown}
          className={`z-10 relative peer border border-light-stroke-primary rounded-full w-full p-2 pl-4 pr-[94px] bg-light-background-neutral-variant focus:outline-none focus:ring-0 focus:outline-offset-[-1px] focus:outline-light-stroke-primary-variant placeholder:p3 placeholder:text-light-fill-primary-variant p3-medium`}
          style={{
            boxShadow:
              searchInputValue && searchInputValue.length >= 3
                ? 'none'
                : '0 2px 10px 0px rgba(0,0,0,0.1)',
          }}
          placeholder={searchBarPlaceholderText || 'Search...'}
          value={searchInputValue}
          autoComplete="on"
          maxLength={100}
          onChange={(e) => {
            setSearchInputValue(e.target.value);
            handleInputChange(e);
          }}
        />
        <div
          className={`absolute top-0 right-12 bottom-0 z-10 ${
            searchInputValue?.length && searchInputValue !== '' ? '' : 'hidden'
          }`}
        >
          <button
            className="flex items-center justify-center w-10 h-full"
            type="button"
            onClick={handleClearSearchInput}
          >
            <span className="sr-only">Clear Search Input</span>
            <SVG svg="icon-close" hidden={false} className="[&_svg]:w-3 [&_svg]:h-3"></SVG>
          </button>
        </div>
        <div className="absolute top-0 right-0 bottom-0 z-10">
          <button
            className="flex items-center justify-center 
              w-[46px] h-full 
              rounded-r-full bg-light-background-gradient 
              hover:bg-light-background-gradient-hover
              focus:bg-light-background-gradient-focus  peer-focus:text-light-fill-secondary"
            type="button"
            onClick={(e) => handleSearchButtonClick(e)}
          >
            <span className="sr-only">Search</span>
            <SVG
              svg="icon-search2"
              hidden={false}
              className="[&_svg]:h-5 [&_svg]:w-5 [&_path]:stroke-light-fill-inverse"
            ></SVG>
          </button>
        </div>
        {renderSuggestions()}
      </div>
    </section>
  );
};
const EnhancedSearchLandingWidget = widget(
  SearchComponent,
  WidgetDataType.SEARCH_RESULTS,
  'content'
);
export default EnhancedSearchLandingWidget;
